import React from 'react';
import { TeButton, TeContainer, TeLinearLoader, TeModal } from '@topcon/tuicl-react';
import { useTranslation } from 'react-i18next';

import { Section } from '../../utils/styling';
import { BusinessPartnerForm } from '../forms/BusinessPartnerForm';
import { useRequestBusinessPartner } from '../../pages/RequestBusinessPartner/businessPartnerRequest.queries';
import { useRequestBusinessPartnerForm } from '../../hooks/forms/useRequestBusinessPartnerForm';
import { RequestBusinessPartnerEndCustomerTypeEnum } from '../../api/generated/pm';

const MODAL_WIDTH = 900;

interface EditBusinessPartnerModalProps {
  open: boolean;
  onClose: (shouldUpdate: boolean) => void;
  id: string;
}

const EditBusinessPartnerModal = ({ open, onClose, id }: EditBusinessPartnerModalProps) => {
  const { t } = useTranslation();
  const { data: requestBusinessPartner } = useRequestBusinessPartner(id);

  const {
    formName,
    isPending,
    handleUpdateRequestBusinessPartner,
    setFormFields,
    validateForm,
    formFields,
    clearFields,
  } = useRequestBusinessPartnerForm({
    defaultValues: {
      addressLineOne: requestBusinessPartner?.companyAddress.addressLineOne,
      addressLineTwo: requestBusinessPartner?.companyAddress.addressLineTwo,
      country: requestBusinessPartner?.companyAddress.country,
      city: requestBusinessPartner?.companyAddress.city,
      zipPostalCode: requestBusinessPartner?.companyAddress.zipPostalCode,
      stateProvinceRegion: requestBusinessPartner?.companyAddress.stateProvinceRegion,
      name: requestBusinessPartner?.name,
      type: requestBusinessPartner?.type as RequestBusinessPartnerEndCustomerTypeEnum,
    },
  });

  const formValid = validateForm(formFields);

  const handleClose = () => {
    if (isPending) return;
    clearFields();
    onClose(false);
  };

  const handleUpdate = async () => {
    await handleUpdateRequestBusinessPartner(id);
    onClose(true);
  };
  const modalTitle = t('editCompany');

  return (
    <TeModal
      variant="dark"
      open={open}
      closeMode="button"
      teclose={handleClose}
      modalTitle={modalTitle}
      contentPadding={0}
      closeable={!isPending}
    >
      <TeContainer
        padding={12}
        width={MODAL_WIDTH}
        column
        grow
        backgroundColor="background.primary"
        gap="xs"
        overflow="auto"
        alignItems="center"
      >
        <Section>
          <BusinessPartnerForm
            businessPartnerType="company"
            handleFormSubmit={handleUpdate}
            formFields={formFields}
            setFormFields={setFormFields}
            isPending={isPending}
            formName={formName}
            resetFieldsStatus={!open}
          />
        </Section>
      </TeContainer>
      <EditBusinessPartnerModalActions
        onClose={onClose}
        isPending={isPending}
        onSubmit={handleUpdate}
        isSubmitButtonDisabled={!formValid}
      />

      {isPending && <TeLinearLoader rectangle slot="loader" />}
    </TeModal>
  );
};

interface EditBusinessPartnerModalActionsProps {
  onClose: (shouldUpdate: boolean) => void;
  isPending: boolean;
  onSubmit: () => void;
  isSubmitButtonDisabled: boolean;
}

const EditBusinessPartnerModalActions = ({
  onClose,
  isPending,
  onSubmit,
  isSubmitButtonDisabled,
}: EditBusinessPartnerModalActionsProps) => {
  const { t } = useTranslation();
  return (
    <>
      <TeButton
        text={t('cancel', { ns: 'common' })}
        variant="neutral"
        teclick={() => onClose(false)}
        size="s"
        disabled={isPending}
        slot="tertiary"
      />
      <TeButton
        text={t('saveChanges', { ns: 'users' })}
        variant="primary"
        teclick={onSubmit}
        size="s"
        disabled={isPending || isSubmitButtonDisabled}
        slot="primary"
      />
    </>
  );
};

export default EditBusinessPartnerModal;
